import React, { useCallback, useState } from 'react';
import { Clock, Calendar, Handshake, MapPin } from 'lucide-react';
import { RideType, RideFormData } from '@/types/ride';
import { OnboardingTooltip } from '@/components/ui/onboarding-tooltip';
import { EnhancedButton } from '@/components/ui/enhanced-button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import MapPreview from '../MapPreview';
import LocationInputs from '../ride/LocationInputs';
import { sameEndpoints, type RouteEndpoints } from '../ride/routeEndpoints';
import RideTypeSelector from '../ride/RideTypeSelector';
import SchedulePicker from '../ride/SchedulePicker';
import { getPriceEstimate } from '@parleyride/domain';
import { RouteData } from '@/hooks/map/useRouting';

interface EnhancedRideFormProps {
  onSubmit: (data: RideFormData) => void;
}

/**
 * Booking on one screen.
 *
 * This was a four-step wizard: Locations → Timing → Vehicle → Negotiate, with
 * a Continue between each. Two of those steps had correct defaults already —
 * `rideTab` starts at 'now' and `rideType` at 'economy' — so `canProceedToNext`
 * returned true for both without the rider touching anything. They existed to
 * be clicked past: two addresses and four button presses to request a ride,
 * three of which advanced a step that required no decision.
 *
 * That is Jakob's Law (PRODUCTION_BAR.md §2) as much as anything. Every rider
 * has used a ride-hailing app, and the pattern they know is one screen:
 * pickup, destination, options with prices, confirm. It is also Tesler's Law —
 * the defaults were already right, so the complexity had been defaulted but
 * not taken off the path.
 *
 * Hick's Law asks for ≤5 actions at first paint, and this reads as five:
 * pickup, destination, when, which vehicle (one grouped list, Miller's ≤7),
 * and request. The vehicle list and the map appear only once both endpoints
 * are chosen, so the first paint is lighter still and the prices shown are
 * always derived from a real route.
 *
 * Goal-Gradient no longer applies: it asks for a progress indicator on flows
 * longer than two steps, and there is one step now, so the stepper is gone
 * rather than counting to four for its own sake.
 */
const EnhancedRideForm: React.FC<EnhancedRideFormProps> = ({ onSubmit }) => {
  const [pickupLocation, setPickupLocation] = useState('');
  const [destination, setDestination] = useState('');
  const [scheduleMode, setScheduleMode] = useState<'now' | 'schedule'>('now');
  const [rideDate, setRideDate] = useState<Date | undefined>(new Date());
  const [rideType, setRideType] = useState<RideType>('economy');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [routeData, setRouteData] = useState<RouteData | null>(null);
  // Only set once the rider has finished choosing both endpoints — see
  // LocationInputs.onEndpointsChange. Feeding the map the raw input strings is
  // what made it show "Failed to load route" while someone was mid-word.
  const [endpoints, setEndpoints] = useState<RouteEndpoints | null>(null);

  // The route in the units the pricing engine takes. Derived once so the
  // vehicle prices and the submitted estimate cannot disagree about the trip.
  const routeKm = routeData ? Math.ceil(routeData.distance / 1000) : null;
  const routeMin = routeData ? Math.ceil(routeData.duration / 60) : null;

  // Stable identity: passed to MapPreview's onRouteCalculated, which lives in
  // its route-processing effect deps. An inline handler would re-run that
  // effect every render and flood the geocoding edge function.
  const handleRouteCalculated = useCallback((calculatedRoute: RouteData) => {
    setRouteData(calculatedRoute);
  }, []);

  // Stable identity for the same reason: it sits in LocationInputs' effect deps.
  const handleEndpointsChange = useCallback((next: RouteEndpoints | null) => {
    setEndpoints((prev) => (sameEndpoints(prev, next) ? prev : next));
  }, []);

  const canRequest = Boolean(pickupLocation && destination);

  const handleSubmit = async () => {
    if (!canRequest) return;

    setIsSubmitting(true);
    const priceEstimate = getPriceEstimate(rideType, routeKm, routeMin);

    const rideData: RideFormData = {
      pickupLocation,
      dropoffLocation: destination,
      destination,
      rideType,
      date: new Date(),
      isScheduled: scheduleMode === 'schedule',
      scheduledTime: scheduleMode === 'schedule' ? rideDate : undefined,
      estimatedPrice: priceEstimate.amount,
      estimatedTime: priceEstimate.time,
    };

    try {
      onSubmit(rideData);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary" />
            Where to?
          </CardTitle>
          <OnboardingTooltip
            title="How Negotiation Works"
            content="ParleyRide lets you negotiate prices with drivers. You'll see their initial offer and can counter-offer until you both agree on a fair price."
            showOnFirstVisit={true}
            storageKey="negotiation-help-seen"
          >
            <span></span>
          </OnboardingTooltip>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        <LocationInputs
          pickupLocation={pickupLocation}
          destination={destination}
          setPickupLocation={setPickupLocation}
          setDestination={setDestination}
          onEndpointsChange={handleEndpointsChange}
        />

        {/*
          When to go. Progressive disclosure rather than a step of its own:
          "Now" is what almost every rider wants and is already selected, so
          the date picker stays out of the way until someone asks for it.
        */}
        <div className="space-y-3">
          <div
            className="inline-flex rounded-md border p-0.5"
            role="group"
            aria-label="When do you need this ride?"
          >
            {(['now', 'schedule'] as const).map((mode) => (
              <Button
                key={mode}
                type="button"
                size="sm"
                variant={scheduleMode === mode ? 'secondary' : 'ghost'}
                aria-pressed={scheduleMode === mode}
                onClick={() => setScheduleMode(mode)}
                className="gap-1.5"
              >
                {mode === 'now' ? (
                  <>
                    <Clock className="h-4 w-4" /> Now
                  </>
                ) : (
                  <>
                    <Calendar className="h-4 w-4" /> Schedule
                  </>
                )}
              </Button>
            ))}
          </div>

          {scheduleMode === 'schedule' && (
            <div className="animate-fade-in">
              <SchedulePicker rideDate={rideDate} setRideDate={setRideDate} />
            </div>
          )}
        </div>

        {/*
          The map and the vehicle prices arrive together, once both endpoints
          are settled: before that there is no route, so any price shown would
          be a generic per-class figure dressed up as an estimate for a trip.
        */}
        {endpoints && (
          <div className="animate-fade-in space-y-5">
            <MapPreview
              from={endpoints.from}
              to={endpoints.to}
              fromCoords={endpoints.fromCoords}
              toCoords={endpoints.toCoords}
              className="h-56 sm:h-72 w-full rounded-lg overflow-hidden"
              showEta={true}
              onRouteCalculated={handleRouteCalculated}
            />

            <RideTypeSelector
              rideType={rideType}
              setRideType={setRideType}
              distanceKm={routeKm}
              durationMin={routeMin}
            />
          </div>
        )}

        {/* Von Restorff: the only primary action on the screen. */}
        <EnhancedButton
          onClick={handleSubmit}
          disabled={!canRequest}
          loading={isSubmitting}
          loadingText="Finding drivers…"
          icon={<Handshake className="h-4 w-4" />}
          className="w-full bg-gradient-to-r from-primary to-secondary hover:opacity-90"
          size="lg"
        >
          Request &amp; negotiate
        </EnhancedButton>

        {!endpoints && (
          <p className="text-sm text-muted-foreground text-center">
            Choose a pickup and a destination to see the route and fares.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default EnhancedRideForm;
